import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { isMainList, isTomorrowList } from '../constants/listTypes';
import './ListSelection.css';

export default function ListSelection({ lists, onSelectList, onCreateNew }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Get the return path from location state, default to '/'
  const returnPath = location.state?.returnPath || '/';

  // Derive activeListId from return path (e.g., /list/123 -> 123)
  const activeListId = returnPath.startsWith('/list/')
    ? returnPath.split('/list/')[1]
    : null;

  // Lists are already filtered by App.js based on tomorrow cutoff preference
  const visibleLists = lists;
  
  const handleSelectList = (listId) => {
    // Scroll to top instantly before navigation
    const mainContent = document.querySelector('.main-content');
    if (mainContent) {
      mainContent.scrollTop = 0;
      mainContent.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    
    // Find the list
    const selectedList = visibleLists.find(l => l.id === listId);
    if (selectedList) {
      onSelectList(listId);
      navigate(`/list/${listId}`);
    } else {
      // Fallback to old behavior
      onSelectList(listId);
      navigate(returnPath);
    }
  };
  
  const handleCreateNew = () => {
    onCreateNew();
    navigate(returnPath);
  };
  
  const handleBack = () => {
    navigate(returnPath);
  };

  return (
    <div className="list-selection-page">
      <div className="list-selection-header">
        <button
          onClick={handleBack}
          className="list-selection-back-button"
          aria-label="Back"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 4l-6 6 6 6" />
          </svg>
        </button>
        <h1 className="list-selection-title">Select List</h1>
        <div style={{ width: '40px' }} /> {/* Spacer for centering */}
      </div>

      <div className="list-selection-content">
        <div className="list-selection-list">
          {visibleLists.map(list => {
            const isMain = isMainList(list.list_type);
            const isTomorrow = isTomorrowList(list.list_type);
            const isActive = list.id === activeListId;
            const openCount = (list.tasks || []).filter(task => !task.completed).length;
            const displayName = isTomorrow ? 'To Do Tomorrow' : list.name;

            return (
              <button
                key={list.id}
                onClick={() => handleSelectList(list.id)}
                className={`list-selection-item ${isActive ? 'list-selection-item-active' : ''} ${isMain ? 'list-selection-item-main' : ''} ${isTomorrow ? 'list-selection-item-tomorrow' : ''}`}
                aria-label={`Select ${displayName}`}
              >
                <div className="list-selection-item-content">
                  <div className="list-selection-item-info">
                    <span className="list-selection-item-name">
                      {displayName}
                      {list.is_shared && (
                        <svg 
                          className="shared-icon" 
                          width="16" 
                          height="16" 
                          viewBox="0 0 20 20" 
                          fill="currentColor"
                          title="Shared list"
                        >
                          <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                        </svg>
                      )}
                    </span>
                    <span className="list-selection-item-count">
                      {openCount} {openCount === 1 ? 'task' : 'tasks'}
                    </span>
                  </div>
                  {isActive && (
                    <svg className="list-selection-checkmark" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        <button
          onClick={handleCreateNew}
          className="list-selection-create-button"
          aria-label="Create new list"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          <span>Create New List</span>
        </button>
      </div>
    </div>
  );
}

